import React, {useEffect, useState} from 'react'
import {useDispatch, useSelector} from 'react-redux'
import {useNavigate} from 'react-router-dom'
import {FiBookOpen, FiFileText, FiLayers, FiPlus} from 'react-icons/fi'
import {
  VmoCard,
  VmoCardContent,
  VmoCreateFirst,
  VmoGrid,
  VmoGridColumn,
  VmoHeader,
  VmoHeaderContent,
  VmoHeaderSubheader,
  VmoIcon,
  VmoLoader,
  VmoTooltip,
} from 'vmo-library'

import {CREATE_KNOWLEDGEBASE, GET_KNOWLEDGEBASE} from 'actions/types'
import {getKnowledgebase, setMetaData} from 'actions'
import {getMetadataInfo, getRouteState} from 'utils/local-storage'
import LockPermissionTooltip from 'components/common/LockPermissionTooltip'
import useKnowledgebase from 'hooks/pure/useKnowledgebase'
import CreateKnowledgebase from 'components/pages/Knowledgebase/CreateKnowledgebase'
import PageLoader from 'components/common/PageLoader'
import useApiLoader from 'hooks/pure/useApiLoader'
import {getPermissionTooltipContent} from 'utils/helper'
import useUserPermissions from 'hooks/pure/useUserPermission'
import {KNOWLEDGEBASE_PATH} from 'constants/path'
import useApiResponse from 'hooks/impure/useApiResponse'

const items = [
  {
    header: 'Keep all informative documents at one place',
    subHeader: 'Document all customer problems , FAQ along with solutions at one place.',
  },
  {
    header: 'Customize your help center',
    subHeader: 'Customize and give a branded look to your help center with all the features.',
  },
  {
    header: 'Deliver consistent customer support',
    subHeader: '24/7 availability and customers can access information when they required.',
  },
]

function Multiknowledgebase() {
  const {setKnowledgeBaseId, setKnowledgeBaseName} = useKnowledgebase()
  const [iscreateModalOpen, setCreateModalOpen] = useState(false)
  const dispatch = useDispatch()
  const navigate = useNavigate()

  const {isLoadingData, type, successLabels = []} = useSelector(state => state.apiReducer)

  const {docsPermissions} = useUserPermissions()
  const allowCustomizeDocsRole = docsPermissions?.customize_docs

  const {loading} = useApiLoader({label: GET_KNOWLEDGEBASE})

  const {data: kbData = []} = useApiResponse({
    action: getKnowledgebase,

    enabled: true,
    label: GET_KNOWLEDGEBASE,
    storePath: 'knowledgebase.kbData',
    onSuccess: (kbData = []) => {
      if (!kbData[0]) {
        dispatch({type: 'EMPTY_GET_DOC_DETAILS'})
      }
      if (kbData && kbData?.length === 1) {
        navigate(`/${KNOWLEDGEBASE_PATH.PREFIX}/${kbData[0]?.id}`, {
          state: {
            ...getRouteState(),
            kbName: kbData[0]?.name,
          },
        })
      } else {
        navigate(`/${KNOWLEDGEBASE_PATH.PREFIX}`)
      }
    },
  })

  useEffect(() => {
    if (successLabels.includes(CREATE_KNOWLEDGEBASE)) {
      dispatch(getKnowledgebase())
      if (!getMetadataInfo().docsStatus) {
        dispatch(
          setMetaData({
            docsStatus: true,
          })
        )
      }
    }
  }, [successLabels, dispatch])

  const renderCreateKnowledgebaseModal = () => {
    return <CreateKnowledgebase open={iscreateModalOpen} toggle={setCreateModalOpen} />
  }

  const renderKbCard = () => {
    return kbData.map(kb => {
      return (
        <VmoTooltip
          key={kb.id}
          content={`${kb.name} knowledgebase`}
          size="small"
          position="top center"
          trigger={
            <VmoCard
              onClick={() => {
                navigate(`/${KNOWLEDGEBASE_PATH.PREFIX}/${kb.id}`, {
                  state: {
                    ...getRouteState(),
                    kbName: kb.name,
                  },
                })
                dispatch({type: 'SET_CURRENT_KB', payload: kb.id})
                setKnowledgeBaseId(kb.id)
                setKnowledgeBaseName(kb.name)
              }}
            >
              <VmoCardContent>
                <VmoHeader>
                  <VmoIcon>
                    <FiBookOpen />
                  </VmoIcon>
                  <VmoHeaderContent>
                    {kb.name}
                    <VmoHeaderSubheader>{kb.detail}</VmoHeaderSubheader>
                  </VmoHeaderContent>
                </VmoHeader>
              </VmoCardContent>
              <VmoCardContent className="KbCardFooter">
                <FiFileText className="ml-0" />
                {kb.articleCount}
                <FiLayers />
                {kb.categoryCount}
              </VmoCardContent>
            </VmoCard>
          }
        />
      )
    })
  }

  if (loading) {
    return <PageLoader />
  }

  if (!kbData?.length) {
    return (
      <div className="ml-5 mt-5">
        {iscreateModalOpen && renderCreateKnowledgebaseModal()}
        <VmoCreateFirst
          headerIcon={<FiFileText />}
          header="Knowledgebase"
          subHeader="Knowledge base is a self-serve customer service library that includes answers to frequently asked questions."
          items={items}
          primaryButtonProps={{
            buttonText: 'Create Knowledgebase',
            onClick: () => setCreateModalOpen(true),
            tooltip: !allowCustomizeDocsRole,
            tooltipContent: getPermissionTooltipContent({roleAccess: allowCustomizeDocsRole}),
          }}
        />
      </div>
    )
  }

  return isLoadingData && type.includes(GET_KNOWLEDGEBASE) ? (
    <VmoLoader active />
  ) : (
    <>
      {iscreateModalOpen && renderCreateKnowledgebaseModal()}
      <div style={{width: '100%', height: '100%'}} className="card-page">
        <VmoGrid centered className="KnowledgebaseGrid m-0 ">
          <VmoGridColumn width="12">
            <div className="kbCard kbCardResponsive">
              {renderKbCard()}
              <LockPermissionTooltip isRoleAccessDenied={!allowCustomizeDocsRole}>
                <VmoCard onClick={() => allowCustomizeDocsRole && setCreateModalOpen(true)}>
                  <VmoCardContent className="d-flex">
                    <VmoHeader>
                      <VmoIcon>
                        <FiPlus />
                      </VmoIcon>
                      <VmoHeaderContent>Create Knowledgebase</VmoHeaderContent>
                    </VmoHeader>
                  </VmoCardContent>
                </VmoCard>
              </LockPermissionTooltip>
            </div>
          </VmoGridColumn>
        </VmoGrid>
      </div>
    </>
  )
}

export default Multiknowledgebase
